import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, callRPC, isSessaoIsolada, soltarSessaoIsolada } from '../lib/supabaseClient';
import { webhookBase } from '../lib/webhookBase';
import { triggerWelcomeEmail } from '../lib/emailService';
import type { Profile } from '../types/database';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: (redirectPath?: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, firstName: string, lastName: string, preferredLanguage?: 'pt' | 'en') => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Alcance do "Sair": SÓ este aparelho (este navegador).
 *
 * Sem argumento, o `supabase.auth.signOut()` sai com alcance GLOBAL: revoga
 * todas as sessões da conta, em todos os aparelhos. Em 03/11/2026, às 11:07 ET,
 * um "Sair" numa conta usada no Mac da clínica derrubou quem usava a mesma
 * conta num Mac no Brasil, e 3 respostas de WhatsApp dela deram 401 e não
 * saíram. Todo signOut do portal passa este objeto; o teste
 * AuthContext.sair.test.tsx reprova qualquer `auth.signOut(` sem ele.
 */
export const SAIR_SO_DESTE_APARELHO = { scope: 'local' } as const;

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Detectar idioma preferido do navegador
  const detectBrowserLanguage = (): 'pt' | 'en' => {
    const browserLang = navigator.language.toLowerCase();
    return browserLang.startsWith('pt') ? 'pt' : 'en';
  };

  // Criar perfil para usuário que fez login via Google (primeira vez)
  const createProfileForOAuthUser = useCallback(async (user: User): Promise<Profile | null> => {
    try {
      const metadata = user.user_metadata || {};
      const email = user.email || '';

      // Extrair nome do Google ou usar email como fallback
      const fullName = metadata.full_name || metadata.name || email.split('@')[0];
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || 'Usuário';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Detectar idioma do navegador
      const preferredLanguage = detectBrowserLanguage();

      const newProfile = {
        id: user.id,
        email: email,
        first_name: firstName,
        last_name: lastName,
        role: 'patient' as const,
        patient_type: 'new' as const,
        avatar_url: metadata.avatar_url || metadata.picture || null,
        preferred_language: preferredLanguage,
      };

      const { data, error } = await supabase
        .from('profiles')
        .insert(newProfile)
        .select()
        .single();

      if (error) {
        console.error('Error creating profile for OAuth user:', error);
        return null;
      }

      console.log(`[Auth] Perfil criado para usuário Google com idioma: ${preferredLanguage}`);
      return data as Profile;
    } catch (error) {
      console.error('Error creating profile for OAuth user:', error);
      return null;
    }
  }, []);

  // Buscar perfil do usuário (ou criar se não existir para OAuth)
  const fetchProfile = useCallback(async (userId: string, user?: User): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // Se não encontrou o perfil e temos dados do usuário (provavelmente OAuth)
        if (error.code === 'PGRST116' && user) {
          console.log('[Auth] Perfil não encontrado, criando para usuário OAuth...');
          const newProfile = await createProfileForOAuthUser(user);
          if (newProfile) {
            setProfile(newProfile);
            return true;
          }
        }
        console.error('Error fetching profile:', error.code, error.message);
        setProfile(null);
        return false;
      }

      // Sincronizar avatar do Google OAuth (sempre atualiza para manter foto atual)
      const googleAvatar = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;
      if (googleAvatar && data.avatar_url !== googleAvatar) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ avatar_url: googleAvatar })
          .eq('id', userId);

        if (!updateError) {
          data.avatar_url = googleAvatar;
        }
      }

      // Auto-detect language for new Google OAuth users (trigger creates profile without preferred_language)
      if (!data.preferred_language) {
        const detectedLang = detectBrowserLanguage();
        const { error: langError } = await supabase
          .from('profiles')
          .update({ preferred_language: detectedLang })
          .eq('id', userId);

        if (!langError) {
          data.preferred_language = detectedLang;
          console.log(`[Auth] Auto-detected language for new user: ${detectedLang}`);
        }
      }

      setProfile(data);
      return true;
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
      return false;
    }
  }, [createProfileForOAuthUser]);

  // Refresh do perfil
  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id, user);
    }
  }, [user, fetchProfile]);

  // Inicializar autenticação
  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          setSession(session);
          setUser(session.user);
          await fetchProfile(session.user.id, session.user);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listener para mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth event:', event);

        // Match por email no login (fire-and-forget, idempotente): quem entra
        // — inclusive via Google OAuth, que volta por redirect e não passa pelo
        // submit do LoginPage — com o MESMO email do cadastro da clínica vira
        // dono do registro OptiMantra correspondente. O servidor só vincula
        // contas de paciente (guard de role em portalLogin.ts).
        if (event === 'SIGNED_IN' && session?.access_token) {
          fetch(`${webhookBase()}/api/portal-login/link`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}` },
          }).catch(() => {});
        }

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Fetch profile mas não bloqueia o loading
          fetchProfile(session.user.id, session.user).finally(() => {
            setLoading(false);
          });
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Sign In
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) return { error };
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Sign In with Google
  const signInWithGoogle = async (redirectPath?: string) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + (redirectPath || ''),
        },
      });

      if (error) return { error };
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Sign Up
  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    preferredLanguage: 'pt' | 'en' = 'pt'
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            preferred_language: preferredLanguage,
          },
        },
      });

      if (error) return { error };

      // Criar perfil após signup
      if (data.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            email,
            first_name: firstName,
            last_name: lastName,
            role: 'patient',
            patient_type: 'new',
            preferred_language: preferredLanguage,
          });

        if (profileError) {
          console.error('Error creating profile:', profileError);
        } else {
          // Send welcome email (fire-and-forget)
          triggerWelcomeEmail({
            email,
            firstName,
            lastName,
            language: preferredLanguage,
          }).catch(() => {});
        }
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  // Sign Out
  const signOut = async () => {
    // Sair vale SÓ para este aparelho, em qualquer aba (ver SAIR_SO_DESTE_APARELHO).
    // Na aba "Portal do paciente" (admin vendo como o paciente) isso já era
    // assim; agora a aba comum segue a mesma regra.
    const eraIsolada = isSessaoIsolada();
    await supabase.auth.signOut(SAIR_SO_DESTE_APARELHO);
    sessionStorage.removeItem('patientPortalSession');
    // Sair na aba isolada tem que apagar TAMBÉM a marca de isolamento: sem isso
    // a aba continuava "vendo como paciente" depois do logout (auditoria 24/set).
    if (eraIsolada) soltarSessaoIsolada();
    setUser(null);
    setProfile(null);
    setSession(null);
  };

  const value: AuthContextType = {
    user,
    profile,
    session,
    loading,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
